import { useState, useEffect, useRef } from "react"
import { object, node, array, func, bool } from "prop-types"
import { Elements } from "@stripe/react-stripe-js"
import ExpressCheckout from "./ExpressCheckout"
import { Icon, Accordion, CenterLabelBorder } from "source/shared/components"
import { SavedPaymentMethodSelector } from "source/payments/components"
import { useEnv } from "source/shared/hooks/useEnv"

const cardHeaderStyles = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
}

const CustomPaymentMethods = ({
  stripe,
  paymentIntent: paymentIntentFromProps,
  stripeForm,
  savedPaymentMethods,
  onPaymentIntentUpdate = () => {},
  onCanMakePayment = () => {},
  mutatePaymentIntent,
  canMakePayment,
}) => {
  const [paymentIntent, setPaymentIntent] = useState(paymentIntentFromProps)
  const [expressPaymentMethods, setExpressPaymentMethods] = useState(null)
  const [expressCheckoutLoading, setExpressCheckoutLoading] = useState(true)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    paymentIntent?.paymentMethod?.id
      ? savedPaymentMethods.find(
          (pm) => pm.stripeIdentifier === paymentIntent.paymentMethod.id,
        )
      : savedPaymentMethods[0],
  )
  const [selectedPanel, setSelectedPanel] = useState(
    savedPaymentMethods?.length ? "savedPayments" : "newCard",
  )

  useEffect(() => {
    if (selectedPanel === "savedPayments" && !selectedPaymentMethod) {
      setTimeout(() => {
        setSelectedPaymentMethod(savedPaymentMethods[0])
      }, 10)
    }
  }, [
    selectedPanel,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    savedPaymentMethods,
  ])

  useEffect(() => {
    setPaymentIntent(paymentIntentFromProps)
  }, [paymentIntentFromProps])

  const onExpressCheckoutReady = ({ availablePaymentMethods }) => {
    if (
      availablePaymentMethods &&
      Object.values(availablePaymentMethods).some((val) => val === true)
    ) {
      setExpressPaymentMethods(availablePaymentMethods)
    }
    setExpressCheckoutLoading(false)
  }

  const onPaymentMethodChange = async (paymentMethod) => {
    setSelectedPaymentMethod(paymentMethod)

    if (paymentMethod?.stripeIdentifier) {
      onCanMakePayment(false)
      const response = await mutatePaymentIntent({
        updated_payment_method: paymentMethod.stripeIdentifier,
      })
      setPaymentIntent(response)
      onPaymentIntentUpdate(response)
      onCanMakePayment(true)
    }
  }

  const paymentFormOptions = {
    appearance: {
      theme: "stripe",
      variables: {
        borderRadius: "4px",
      },
      rules: {
        ".Input": {
          boxShadow: "inset 0 1px 2px rgb(250, 250, 250)",
          border: "1px solid rgb(224 224 224)",
          padding: "12px",
        },
        ".Input--invalid": {
          border: "1px solid rgb(224 224 224)",
          boxShadow: "inset 0 1px 2px rgb(250, 250, 250)",
        },
        ".Label": {
          fontWeight: 500,
          marginBottom: "8px",
          fontSize: "14px",
          color: "#3d3d3d",
        },
        ".Error": {
          marginTop: "8px",
          color: "rgb(239, 80, 78)",
          fontSize: "16px",
        },
      },
    },
    wallets: {
      applePay: "auto",
      googlePay: "auto",
      link: "never",
    },
  }

  const handlePanelChange = async (panelName) => {
    if ((panelName === null || panelName) == "savedPayments") {
      savedPaymentMethodSelectorRef.current?.reset()
    }
    onCanMakePayment(false)
    setSelectedPanel(panelName)
    const response = await mutatePaymentIntent({
      updated_payment_method: "",
      payment_method_types:
        panelName === null || panelName == "newCard"
          ? ["card"]
          : ["card", "us_bank_account"],
    })
    setPaymentIntent(response)
    onPaymentIntentUpdate(response)
    if (panelName == "newCard") {
      onCanMakePayment(true)
    }
  }

  const savedPaymentMethodSelectorRef = useRef(null)

  const savedPaymentsConfig = {
    name: "savedPayments",
    heading: (
      <div css={cardHeaderStyles}>
        <Icon symbol="giving#payment-methods" />
        <div>Saved Payment Methods</div>
      </div>
    ),
    content: (
      <SavedPaymentMethodSelector
        ref={savedPaymentMethodSelectorRef}
        paymentMethods={savedPaymentMethods}
        onPaymentMethodChange={onPaymentMethodChange}
        initialSelectedPaymentMethod={selectedPaymentMethod}
      />
    ),
  }

  const newCardConfig = {
    name: "newCard",
    heading: (
      <div css={cardHeaderStyles}>
        <Icon symbol="general#outlined-credit-card" />
        <div>New Card</div>
      </div>
    ),
    content: (
      <Elements
        stripe={stripe}
        options={{
          ...paymentFormOptions,
          clientSecret: paymentIntent?.clientSecret,
          customerSessionClientSecret:
            paymentIntent?.customerSessionClientSecret,
        }}
      >
        {stripeForm}
      </Elements>
    ),
  }

  const buildAccordionPanels = () => {
    const panels = [newCardConfig]
    if (savedPaymentMethods?.length) panels.unshift(savedPaymentsConfig)
    return panels
  }

  const env = useEnv()

  const paymentMethodTypeOptions =
    env === "development" ? ["card", "amazon_pay"] : ["card"]

  return (
    <>
      {(expressCheckoutLoading || expressPaymentMethods) && (
        <>
          <Elements
            stripe={stripe}
            options={{
              ...paymentFormOptions,
              amount: paymentIntent?.amount,
              mode: "payment",
              currency: paymentIntent?.currency?.toLowerCase(),
              paymentMethodTypes: paymentMethodTypeOptions,
            }}
          >
            <ExpressCheckout
              stripe={stripe}
              paymentIntent={paymentIntent}
              loading={expressCheckoutLoading}
              onExpressCheckoutReady={onExpressCheckoutReady}
              canMakePayment={canMakePayment}
            />
          </Elements>
          <CenterLabelBorder className="my-3" label="or" />
        </>
      )}
      <Accordion
        panels={buildAccordionPanels()}
        defaultOpenPanel={selectedPanel}
        onPanelChange={handlePanelChange}
      />
    </>
  )
}

CustomPaymentMethods.propTypes = {
  stripe: object.isRequired,
  paymentIntent: object.isRequired,
  stripeForm: node.isRequired,
  savedPaymentMethods: array.isRequired,
  onPaymentIntentUpdate: func,
  onCanMakePayment: func,
  mutatePaymentIntent: func.isRequired,
  canMakePayment: bool,
}

export default CustomPaymentMethods
